<!-- TimeseriesPanel.vue -->
<!-- Combined timeseries modal: Import tab + Browse/Query tab -->
<template>
  <div class="ts-panel" v-if="visible">
    <div class="ts-overlay" @click.self="handleOverlayClick">
      <div class="ts-modal">

        <!-- Header -->
        <div class="ts-header">
          <div class="ts-tabs">
            <button
              class="ts-tab"
              :class="{ active: activeTab === 'browse' }"
              @click="switchTab('browse')"
              :disabled="isBusy"
            >
              <i class="bi bi-search"></i> Browse
            </button>
            <button
              class="ts-tab"
              :class="{ active: activeTab === 'import' }"
              @click="switchTab('import')"
              :disabled="isBusy"
            >
              <i class="bi bi-cloud-upload"></i> Import
            </button>
          </div>
          <button class="close-btn" @click="handleClose" :disabled="isBusy">
            <i class="bi bi-x-lg"></i>
          </button>
        </div>

        <!-- ═══════════════════════════════════════════════════ -->
        <!-- BROWSE TAB                                          -->
        <!-- ═══════════════════════════════════════════════════ -->
        <div v-if="activeTab === 'browse'" class="ts-browse">

          <!-- Loading available templates -->
          <div v-if="availLoading" class="center-spinner">
            <i class="bi bi-arrow-repeat spinner"></i> Loading available timeseries…
          </div>

          <div v-else-if="availError" class="alert-error">
            <i class="bi bi-exclamation-triangle"></i> {{ availError }}
          </div>

          <template v-else-if="availSeries.length === 0">
            <p class="empty-hint">No timeseries data has been imported for this experiment yet.</p>
          </template>

          <template v-else>
            <!-- Fixed controls section -->
            <div class="browse-controls">
              <!-- Time range filter -->
              <div class="filter-row">
                <div class="filter-group">
                  <label>From</label>
                  <input type="datetime-local" v-model="timeFrom" class="form-control sm" />
                </div>
                <div class="filter-group">
                  <label>To</label>
                  <input type="datetime-local" v-model="timeTo" class="form-control sm" />
                </div>
                <div class="filter-group">
                  <label>Limit</label>
                  <input type="number" v-model.number="queryLimit" min="1" max="10000" class="form-control sm w-limit" />
                </div>
                <div class="filter-group">
                  <label>Order</label>
                  <select v-model="queryOrder" class="form-control sm">
                    <option value="asc">Asc</option>
                    <option value="desc">Desc</option>
                  </select>
                </div>
              </div>

              <!-- Attribute filters -->
              <div class="attr-filters">
                <div class="attr-filters-header">
                  <span class="section-label">Attribute Filters</span>
                  <button class="btn-add-filter" @click="addAttributeFilter">
                    <i class="bi bi-plus-circle"></i> Add filter
                  </button>
                </div>
                <div
                  v-for="(f, idx) in attributeFilters"
                  :key="idx"
                  class="attr-filter-row"
                >
                  <select v-model="f.attribute" class="form-control sm" @change="onFilterAttributeChange(f)">
                    <option value="" disabled>Select attribute…</option>
                    <option v-for="key in allAvailableKeys" :key="key.variable" :value="key.variable">
                      {{ key.variable }}
                    </option>
                  </select>

                  <!-- number: min / max -->
                  <template v-if="getFilterType(f.attribute) === 'number'">
                    <input type="number" v-model.number="f.min" placeholder="Min" class="form-control sm narrow" />
                    <input type="number" v-model.number="f.max" placeholder="Max" class="form-control sm narrow" />
                  </template>

                  <!-- string: contains -->
                  <template v-else-if="getFilterType(f.attribute) === 'string'">
                    <input type="text" v-model="f.contains" placeholder="Contains…" class="form-control sm w-contains" />
                  </template>

                  <!-- boolean: yes / no / any -->
                  <template v-else-if="getFilterType(f.attribute) === 'boolean'">
                    <select v-model="f.equals" class="form-control sm w-bool">
                      <option :value="null">Any</option>
                      <option :value="true">Yes</option>
                      <option :value="false">No</option>
                    </select>
                  </template>

                  <!-- date / datetime: from / to -->
                  <template v-else-if="getFilterType(f.attribute) === 'date'">
                    <input type="datetime-local" v-model="f.dateFrom" class="form-control sm w-date" />
                    <input type="datetime-local" v-model="f.dateTo" class="form-control sm w-date" />
                  </template>

                  <!-- fallback (unknown type): just contains -->
                  <template v-else>
                    <input type="text" v-model="f.contains" placeholder="Contains…" class="form-control sm w-contains" />
                  </template>

                  <button class="btn-icon-danger" @click="removeAttributeFilter(idx)" title="Remove">
                    <i class="bi bi-x"></i>
                  </button>
                </div>
              </div>

              <!-- Run query button -->
              <div class="query-actions">
                <button class="btn-primary" @click="runQuery(false)" :disabled="queryLoading">
                  <i v-if="queryLoading" class="bi bi-arrow-repeat spinner"></i>
                  <i v-else class="bi bi-play-fill"></i>
                  {{ queryLoading ? 'Querying…' : 'Run Query' }}
                </button>
                <button v-if="queryResults.length" class="btn-secondary" @click="clearResults">
                  Clear
                </button>
              </div>

              <!-- Query error -->
              <div v-if="queryError" class="alert-error">
                <i class="bi bi-exclamation-triangle"></i> {{ queryError }}
              </div>

              <!-- Available series summary -->
              <div class="avail-summary">
                <span class="section-label">Available series:&nbsp;</span>
                <span v-for="(s, si) in availSeries" :key="s.id" class="avail-inline">
                  <strong>{{ s.name }}</strong>
                  <span v-if="si < availSeries.length - 1">&nbsp;&bull;&nbsp;</span>
                </span>
              </div>
            </div>

            <!-- Scrollable results pane -->
            <div v-if="queryResults.length" class="browse-results">
              <div class="results-meta">
                <span>{{ queryResults.length }} row{{ queryResults.length !== 1 ? 's' : '' }}</span>
                <span v-if="queryHasMore" class="has-more-hint">More results available</span>
                <button class="btn-graph" @click="showGraph = true" title="Open graph view">
                  <i class="bi bi-graph-up-arrow"></i> Graph
                </button>
              </div>
              <div class="results-table-wrap">
                <table class="results-table">
                  <thead>
                    <tr>
                      <th>Timestamp</th>
                      <th v-for="col in rowColumns" :key="col">{{ col }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(pt, i) in queryResults" :key="i">
                      <td class="ts-cell">{{ formatTs(pt.ts) }}</td>
                      <td v-for="col in rowColumns" :key="col">{{ getRowValues(pt)?.[col] ?? '—' }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div class="results-actions-row">
                <div class="load-more-spacer"></div>
                <button v-if="queryHasMore" class="btn-secondary" @click="runQuery(true)" :disabled="queryLoading">
                  <i v-if="queryLoading" class="bi bi-arrow-repeat spinner"></i>
                  Load more
                </button>
                <div class="load-more-spacer"></div>
                <button class="btn-primary" @click="exportCsv" :disabled="exportLoading">
                  <i v-if="exportLoading" class="bi bi-arrow-repeat spinner"></i>
                  <i v-else class="bi bi-download"></i>
                  Export
                </button>
                <button class="btn-secondary" style="margin-left: 12px;" @click="handleClose">Close</button>
              </div>
            </div>
          </template>
        </div>

        <!-- ═══════════════════════════════════════════════════ -->
        <!-- IMPORT TAB                                          -->
        <!-- ═══════════════════════════════════════════════════ -->
        <div v-if="activeTab === 'import'" class="ts-body">

          <!-- Step indicator -->
          <div class="ts-steps">
            <span
              v-for="(label, idx) in stepLabels"
              :key="idx"
              class="ts-step"
              :class="{ active: importStep === idx, done: importStep > idx }"
            >
              <i class="bi" :class="importStep > idx ? 'bi-check-circle-fill' : 'bi-circle'"></i>
              {{ label }}
            </span>
          </div>

          <!-- Step 0: Setup -->
          <div v-if="importStep === 0">
            <div class="form-group">
              <label>CSV File</label>
              <div class="file-drop-zone" @click="$refs.csvInput.click()" @dragover.prevent @drop.prevent="onFileDrop">
                <i class="bi bi-file-earmark-spreadsheet"></i>
                <span v-if="!csvFile">Click or drag a CSV file here</span>
                <span v-else class="selected-file">{{ csvFile.name }} ({{ formatBytes(csvFile.size) }})</span>
              </div>
              <input ref="csvInput" type="file" accept=".csv,text/csv" style="display:none" @change="onFileSelect" />
            </div>
          </div>

          <!-- Step 1: Uploading + Preview -->
          <div v-if="importStep === 1">
            <div v-if="uploadState !== 'done'" class="upload-progress-section">
              <p class="status-label">
                <i v-if="uploadState === 'uploading'" class="bi bi-arrow-repeat spinner"></i>
                <i v-else-if="uploadState === 'error'" class="bi bi-exclamation-circle-fill text-danger"></i>
                {{ uploadStatusText }}
              </p>
              <div v-if="uploadState === 'uploading'" class="progress-bar">
                <div class="progress-fill" :style="{ width: uploadPercent + '%' }"></div>
              </div>
            </div>
            <div v-if="uploadState === 'done' && previewState === 'running'" class="center-spinner">
              <i class="bi bi-arrow-repeat spinner"></i> Profiling CSV…
            </div>
            <div v-if="previewState === 'error'" class="alert-error">
              <i class="bi bi-exclamation-triangle"></i> {{ previewError }}
            </div>
          </div>

          <!-- Step 2: Review Preview -->
          <div v-if="importStep === 2 && previewResult">
            <h4>CSV Profile</h4>
            <table class="preview-table">
              <tbody>
                <tr><td><strong>Timestamp Column</strong></td>
                  <td>
                    <select v-model="tsColumn" class="form-control" style="width:220px">
                      <option value="" disabled>Select timestamp column…</option>
                      <option v-for="candidate in eligibleTimeCandidates" :key="candidate.column" :value="candidate.column">
                        {{ candidate.column }}
                      </option>
                    </select>
                  </td>
                </tr>
                <tr><td><strong>Timestamp Format</strong></td>
                  <td>
                    <select v-model="tsType" class="form-control" style="width:220px">
                      <option v-for="opt in availableTimeFormats" :key="opt.value" :value="opt.value">
                        {{ opt.label }}
                      </option>
                    </select>
                  </td>
                </tr>
                <tr v-if="selectedTimeCandidate && selectedTimeCandidate.sampleParse">
                  <td><strong>Parse Info</strong></td>
                  <td>
                    <div class="parse-info-list">
                      <div v-for="row in parseInfoRows" :key="row.format" :class="['parse-info-item', row.level]">
                        <span class="fmt">{{ row.label }}</span>
                        <span class="stats">{{ row.text }}</span>
                      </div>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
            <div class="columns-section">
              <div class="columns-section-header">
                <strong>Queryable Columns</strong>
                <span class="col-count">{{ selectedSeriesColumns.length }}/{{ allPointColumns.length }} selected</span>
                <button class="btn-add-filter" @click="selectAllColumns">All</button>
                <button class="btn-add-filter" @click="selectedSeriesColumns = []">None</button>
              </div>
              <p class="hint" style="margin:0 0 8px">
                Mark which columns should be queryable and filterable. <strong>All columns are saved regardless</strong> — this selection only affects query performance.
              </p>
              <input
                v-model="columnSearch"
                type="text"
                class="form-control"
                placeholder="Filter column names…"
                style="margin-bottom:8px"
              />
              <div class="col-picker-table-wrap">
                <table class="col-picker-table">
                  <thead>
                    <tr>
                      <th style="width:32px"></th>
                      <th>Column name</th>
                      <th style="width:110px">Data type</th>
                      <th style="width:130px">Unit <small>(optional)</small></th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="col in pointColumns" :key="col" :class="{ 'col-row-selected': isColSelected(col) }" @click.stop="toggleColumn(col)">
                      <td><input type="checkbox" :checked="isColSelected(col)" @change.stop="toggleColumn(col)" @click.stop /></td>
                      <td class="col-name">{{ col }}</td>
                      <td @click.stop>
                        <select
                          v-if="isColSelected(col)"
                          :value="getColField(col, 'unit_type')"
                          @change="setColField(col, 'unit_type', $event.target.value)"
                          class="form-control sm"
                        >
                          <option value="float">Number</option>
                          <option value="string">Text</option>
                        </select>
                        <span v-else class="muted-type">—</span>
                      </td>
                      <td @click.stop>
                        <input
                          v-if="isColSelected(col)"
                          :value="getColField(col, 'unit')"
                          @input="setColField(col, 'unit', $event.target.value)"
                          type="text"
                          class="form-control sm"
                          placeholder="e.g. °C"
                          @click.stop
                        />
                        <span v-else class="muted-type">—</span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <small v-if="selectedSeriesColumns.length === 0" class="hint text-warn" style="margin-top:6px;display:block">Select at least one column.</small>
            </div>
          </div>

          <!-- Step 3: Import -->
          <div v-if="importStep === 3">
            <div v-if="importState === 'dry-running'" class="center-spinner">
              <i class="bi bi-arrow-repeat spinner"></i> Running dry-run estimate…
            </div>
            <div v-if="dryRunResult && importState !== 'dry-running'">
              <h4>Dry Run Summary</h4>
              <table class="preview-table">
                <tbody>
                  <tr><td><strong>Estimated Points</strong></td><td>{{ dryRunResult.estimated_points ?? dryRunResult.summary?.estimated_points ?? '—' }}</td></tr>
                  <tr v-if="dryRunResult.series_count != null"><td><strong>Series</strong></td><td>{{ dryRunResult.series_count }}</td></tr>
                  <tr v-if="dryRunResult.timestamp_errors"><td><strong>Timestamp Errors</strong></td><td>{{ dryRunResult.timestamp_errors }}</td></tr>
                </tbody>
              </table>
              <p class="hint" v-if="importState === 'idle'">Review the estimate above. Click <strong>Import</strong> to write the data.</p>
            </div>
            <div v-if="importState === 'running'" class="center-spinner">
              <i class="bi bi-arrow-repeat spinner"></i> Importing timeseries data…
            </div>
            <div v-if="importState === 'done' && importResult" class="import-success">
              <i class="bi bi-check-circle-fill"></i>
              <h4>Import Complete</h4>
              <table class="preview-table">
                <tbody>
                  <tr><td><strong>Points Imported</strong></td><td>{{ importResult.points_imported ?? importResult.summary?.points_imported ?? '—' }}</td></tr>
                  <tr v-if="importResult.upload_batch_id"><td><strong>Batch ID</strong></td><td>{{ importResult.upload_batch_id }}</td></tr>
                </tbody>
              </table>
            </div>
            <div v-if="importState === 'error'" class="alert-error">
              <i class="bi bi-exclamation-triangle"></i> {{ importError }}
            </div>
          </div>

        </div>

        <!-- Footer -->
        <div class="ts-footer" v-if="activeTab === 'import'">
          <button class="btn-secondary" @click="handleClose" :disabled="isBusy">
            {{ importState === 'done' ? 'Close' : 'Cancel' }}
          </button>

          <!-- Import tab navigation -->
          <template v-if="activeTab === 'import'">
            <button
              v-if="importStep === 0"
              class="btn-primary"
              @click="startUploadAndPreview"
              :disabled="!canProceedFromSetup"
            >
              Upload &amp; Preview <i class="bi bi-arrow-right"></i>
            </button>
            <button
              v-if="importStep === 2"
              class="btn-primary"
              @click="startDryRun"
              :disabled="isBusy || !canProceedFromReview"
            >
              Estimate Import <i class="bi bi-arrow-right"></i>
            </button>
            <button
              v-if="importStep === 3 && importState === 'idle'"
              class="btn-primary"
              @click="runImport"
              :disabled="isBusy"
            >
              Import <i class="bi bi-cloud-upload"></i>
            </button>
          </template>
        </div>

      </div>
    </div>

    <!-- Graph overlay (separate layer) -->
    <TimeseriesGraph
      :visible="showGraph"
      :queryResults="queryResults"
      :numericKeys="numericColumns"
      :seriesMetaMap="seriesMetaMap"
      :getRowValues="getRowValues"
      @close="showGraph = false"
    />

  </div>
</template>

<script>
import apiService from '@/services/apiService';
import { useToast } from 'vue-toastification';
import { mapState } from 'vuex';
import TimeseriesGraph from '@/components/TimeseriesGraph.vue';

export default {
  name: 'TimeseriesPanel',
  components: { TimeseriesGraph },
  props: {
    visible:    { type: Boolean, default: false },
    entityType: { type: String, default: 'experiment' },
    entityId:   { type: [Number, String], required: true },
    entraId:    { type: String, required: true },
  },
  emits: ['close', 'complete'],
  setup() {
    const toast = useToast();
    return { toast };
  },
  data() {
    return {
      activeTab: 'browse',

      // ── Browse state ──────────────────────────────────────
      availLoading: false,
      availError: '',
      availSeries: [],              // [{id, name, unit, unit_type, value_type, upload_batch_id, point_count, ts_range}]
      timeFrom: '',
      timeTo: '',
      queryLimit: 1000,
      queryOrder: 'asc',
      attributeFilters: [],         // [{ attribute, min, max }]
      queryLoading: false,
      queryError: '',
      queryResults: [],
      queryNextCursor: null,
      queryHasMore: false,
      exportLoading: false,
      showGraph: false,
    
      // ── Import state ──────────────────────────────────────
      importStep: 0,
      stepLabels: ['Setup', 'Upload & Preview', 'Review', 'Import'],

      csvFile: null,
      tsColumn: 'timestamp',
      tsType: 'iso8601',

      uploadState: 'idle',    // idle | uploading | done | error
      uploadPercent: 0,
      uploadStatusText: '',
      uploadedFileId: null,

      previewState: 'idle',   // idle | running | done | error
      previewResult: null,
      previewError: '',

      importState: 'idle',    // idle | dry-running | running | done | error
      dryRunResult: null,
      importResult: null,
      importError: '',

      // columns chosen in step 2 review — array of { name, unit, unit_type }
      selectedSeriesColumns: [],
      columnSearch: '',
      timeCandidates: [],
    };
  },
  computed: {
    ...mapState(['loggedInUser']),
    isBusy() {
      return (
        this.uploadState === 'uploading' ||
        this.previewState === 'running' ||
        this.importState === 'dry-running' ||
        this.importState === 'running' ||
        this.queryLoading ||
        this.exportLoading
      );
    },
    canProceedFromSetup() {
      return !!this.csvFile;
    },
    canProceedFromReview() {
      return this.selectedSeriesColumns.length > 0 && !!this.tsColumn.trim();
    },
    timestampSpec() {
      const column = (this.tsColumn || '').trim();
      if (!column) return null;
      return { column, type: this.tsType || 'iso8601' };
    },
    pointColumns() {
      const detectedColumns = this.previewResult?.detected?.columns || [];
      const timestampColumn = (this.tsColumn || '').trim();
      const base = timestampColumn ? detectedColumns.filter((col) => col !== timestampColumn) : detectedColumns;
      const q = this.columnSearch.trim().toLowerCase();
      return q ? base.filter((col) => col.toLowerCase().includes(q)) : base;
    },
    allPointColumns() {
      const detectedColumns = this.previewResult?.detected?.columns || [];
      const timestampColumn = (this.tsColumn || '').trim();
      return timestampColumn ? detectedColumns.filter((col) => col !== timestampColumn) : detectedColumns;
    },
    eligibleTimeCandidates() {
      return this.timeCandidates.filter((candidate) => candidate.eligible);
    },
    selectedTimeCandidate() {
      const selectedColumn = (this.tsColumn || '').trim();
      if (!selectedColumn) return null;
      return this.timeCandidates.find((candidate) => candidate.column === selectedColumn) || null;
    },
    formatLabelMap() {
      return {
        iso8601: 'Date & time',
        unix_ms: 'Unix timestamp (milliseconds)',
        unix_s: 'Unix timestamp (seconds)',
        relative_s: 'Relative time (seconds)',
      };
    },
    availableTimeFormats() {
      const eligibleFormats = this.selectedTimeCandidate?.eligibleFormats || [];
      if (!eligibleFormats.length) {
        return [
          { value: 'iso8601', label: this.formatLabelMap.iso8601 },
          { value: 'unix_ms', label: this.formatLabelMap.unix_ms },
          { value: 'unix_s', label: this.formatLabelMap.unix_s },
          { value: 'relative_s', label: this.formatLabelMap.relative_s },
        ];
      }
      return eligibleFormats.map((fmt) => ({ value: fmt, label: this.formatLabelMap[fmt] || fmt }));
    },
    parseInfoRows() {
      const parseStats = this.selectedTimeCandidate?.sampleParse;
      if (!parseStats || typeof parseStats !== 'object') return [];

      const candidateFormats = this.selectedTimeCandidate?.eligibleFormats?.length
        ? this.selectedTimeCandidate.eligibleFormats
        : Object.keys(parseStats);

      return candidateFormats
        .filter((fmt) => parseStats[fmt])
        .map((fmt) => {
          const ok = Number(parseStats[fmt]?.ok || 0);
          const fail = Number(parseStats[fmt]?.fail || 0);
          let level = 'muted';
          if (ok > 0 && fail === 0) level = 'good';
          else if (ok > 0) level = 'warn';
          else level = 'bad';

          return {
            format: fmt,
            label: this.formatLabelMap[fmt] || fmt,
            text: `${ok} rows match${fail > 0 ? `, ${fail} do not` : ''}`,
            level,
          };
        });
    },
    allAvailableKeys() {
      return this.availSeries.map(s => ({ variable: s.name, value_type: s.value_type }));
    },
    keyTypeMap() {
      const map = {};
      for (const key of this.allAvailableKeys) {
        map[key.variable] = this.classifyValueType(key.value_type);
      }
      return map;
    },
    seriesMetaMap() {
      const map = {};
      for (const s of this.availSeries) {
        if (!s?.name) continue;
        map[s.name] = {
          unit: s.unit || '',
          unit_type: s.unit_type || s.value_type || '',
        };
      }
      return map;
    },
    // Derive column names from first result item, regardless of nesting
    rowColumns() {
      for (const pt of this.queryResults) {
        const vals = this.getRowValues(pt);
        if (vals && Object.keys(vals).length > 0) return Object.keys(vals);
      }
      return [];
    },
    // Numeric-only keys (integer/float) eligible for graph axes
    numericColumns() {
      // First pass: use template metadata
      const fromMeta = this.rowColumns.filter(col => this.keyTypeMap[col] === 'number');
      if (fromMeta.length > 0) return fromMeta;
      // Fallback: detect from actual data values (first 10 rows)
      const numeric = new Set();
      for (const pt of this.queryResults.slice(0, 10)) {
        const vals = this.getRowValues(pt);
        if (!vals) continue;
        for (const [k, v] of Object.entries(vals)) {
          if (typeof v === 'number' || (typeof v === 'string' && v !== '' && !isNaN(+v))) {
            numeric.add(k);
          }
        }
      }
      return this.rowColumns.filter(col => numeric.has(col));
    },
    hasPanelResults() {
      return this.activeTab === 'browse' && this.queryResults.length > 0;
    },
    modalHeight() {
      return this.hasPanelResults ? '90vh' : 'auto';
    },
  },
  watch: {
    visible(val) {
      if (val) {
        this.resetImport();
        this.fetchAvailable();
      }
    },
  },
  methods: {
    // ── General ────────────────────────────────────────────

    // Extract data-column map from a result item.
    // Handles: { ts, row: {...} }, { ts, values: {...} }, or flat { ts, col1, col2, … }
    getRowValues(pt) {
      if (pt.row && typeof pt.row === 'object') return pt.row;
      if (pt.values && typeof pt.values === 'object') return pt.values;
      if (pt.data && typeof pt.data === 'object') return pt.data;
      // Flat object — exclude known metadata keys
      const SKIP = new Set(['ts', 'timestamp', 'time', 'cursor', 'id', 'entity_id', 'entity_type']);
      const flat = {};
      for (const [k, v] of Object.entries(pt)) {
        if (!SKIP.has(k)) flat[k] = v;
      }
      return flat;
    },

    switchTab(tab) {
      this.activeTab = tab;
    },

    handleClose() {
      if (this.importState === 'done') this.$emit('complete');
      this.$emit('close');
    },

    handleOverlayClick() {
      if (!this.isBusy) this.handleClose();
    },

    formatTs(ts) {
      if (!ts) return '—';
      return new Date(ts).toLocaleString();
    },

    formatBytes(bytes) {
      if (bytes < 1024) return bytes + ' B';
      if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
      return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    },

    // ── Browse ─────────────────────────────────────────────

    async fetchAvailable() {
      this.availLoading = true;
      this.availError = '';
      try {
        const data = await apiService.getAvailableTimeseries(
          this.entityType, this.entityId, this.entraId
        );
        this.availSeries = data?.series || (Array.isArray(data) ? data : []);
      } catch (e) {
        if (e.response?.status === 404) {
          this.availSeries = [];
        } else {
          this.availError = e.response?.data?.detail || e.message || 'Failed to load timeseries info.';
        }
      } finally {
        this.availLoading = false;
      }
    },

    addAttributeFilter() {
      this.attributeFilters.push({
        attribute: '',
        min: null, max: null,
        contains: '',
        equals: null,
        dateFrom: '', dateTo: '',
      });
    },

    onFilterAttributeChange(f) {
      // Reset all filter values when attribute changes
      f.min = null; f.max = null;
      f.contains = ''; f.equals = null;
      f.dateFrom = ''; f.dateTo = '';
    },

    classifyValueType(vt) {
      switch (vt) {
        case 'integer':
        case 'float':
          return 'number';
        case 'boolean':
          return 'boolean';
        case 'date':
        case 'datetime':
          return 'date';
        case 'string':
          return 'string';
        default:
          return 'unknown';
      }
    },

    getFilterType(attribute) {
      if (!attribute) return 'unknown';
      return this.keyTypeMap[attribute] || 'unknown';
    },

    buildActiveFilters() {
      return this.attributeFilters
        .filter(f => f.attribute)
        .map(f => {
          const out = { attribute: f.attribute };
          const type = this.getFilterType(f.attribute);
          if (type === 'number') {
            if (f.min !== null && f.min !== '') out.valueRange = { ...(out.valueRange || {}), min: f.min };
            if (f.max !== null && f.max !== '') out.valueRange = { ...(out.valueRange || {}), max: f.max };
          } else if (type === 'string' || type === 'unknown') {
            if (f.contains) out.contains = f.contains;
          } else if (type === 'boolean') {
            if (f.equals !== null) out.equals = f.equals;
          } else if (type === 'date') {
            if (f.dateFrom) out.from = f.dateFrom;
            if (f.dateTo) out.to = f.dateTo;
          }
          return out;
        });
    },

    removeAttributeFilter(idx) {
      this.attributeFilters.splice(idx, 1);
    },

    clearResults() {
      this.queryResults = [];
      this.queryNextCursor = null;
      this.queryHasMore = false;
      this.queryError = '';
    },

    async exportCsv() {
      this.exportLoading = true;
      try {
        const activeFilters = this.buildActiveFilters();
        const timeRange = (this.timeFrom || this.timeTo)
          ? { from: this.timeFrom || null, to: this.timeTo || null }
          : null;
        const response = await apiService.exportTimeseries({
          entraId: this.entraId,
          entityType: this.entityType,
          entityId: Number(this.entityId),
          attributeFilters: activeFilters.length ? activeFilters : undefined,
          timeRange,
          limit: this.queryLimit,
          order: this.queryOrder,
        });
        const url = URL.createObjectURL(response.data);
        const a = document.createElement('a');
        const disposition = response.headers?.['content-disposition'] || '';
        const match = disposition.match(/filename[^;=\n]*=(['"]?)([^'"\n;]*)\1/);
        a.download = match ? match[2].trim() : `timeseries_export_${this.entityId}.csv`;
        a.href = url;
        a.click();
        URL.revokeObjectURL(url);
      } catch (e) {
        this.toast.error('Export failed: ' + (e.response?.data?.detail || e.message));
      } finally {
        this.exportLoading = false;
      }
    },

    async runQuery(loadMore = false) {
      if (!loadMore) {
        this.queryResults = [];
        this.queryNextCursor = null;
        this.queryHasMore = false;
      }
      this.queryLoading = true;
      this.queryError = '';
      try {
        const activeFilters = this.buildActiveFilters();

        const timeRange = (this.timeFrom || this.timeTo)
          ? { from: this.timeFrom || null, to: this.timeTo || null }
          : null;

        const result = await apiService.queryTimeseries({
          entraId: this.entraId,
          entityType: this.entityType,
          entityId: Number(this.entityId),
          attributeFilters: activeFilters.length ? activeFilters : undefined,
          timeRange,
          limit: this.queryLimit,
          cursor: loadMore ? this.queryNextCursor : null,
          order: this.queryOrder,
        });

        this.queryResults = [...this.queryResults, ...(result.data || [])];
        this.queryNextCursor = result.nextCursor || null;
        this.queryHasMore = result.meta?.hasMore || false;
        if (!loadMore && this.queryResults.length > 0) {
          console.log('[Timeseries] first result item:', JSON.parse(JSON.stringify(this.queryResults[0])));
        }
      } catch (e) {
        this.queryError = e.response?.data?.message || e.response?.data?.detail || e.message || 'Query failed.';
      } finally {
        this.queryLoading = false;
      }
    },

    // ── Import ─────────────────────────────────────────────

    resetImport() {
      this.importStep = 0;
      this.csvFile = null;
      this.tsColumn = 'timestamp';
      this.tsType = 'iso8601';
      this.uploadState = 'idle';
      this.uploadPercent = 0;
      this.uploadStatusText = '';
      this.uploadedFileId = null;
      this.previewState = 'idle';
      this.previewResult = null;
      this.previewError = '';
      this.importState = 'idle';
      this.dryRunResult = null;
      this.importResult = null;
      this.importError = '';
      this.selectedSeriesColumns = [];
      this.columnSearch = '';
      this.timeCandidates = [];
    },

    // ── Column picker helpers ───────────────────────────────
    isColSelected(name) {
      return this.selectedSeriesColumns.some((c) => c.name === name);
    },
    toggleColumn(name) {
      const idx = this.selectedSeriesColumns.findIndex((c) => c.name === name);
      if (idx >= 0) {
        this.selectedSeriesColumns.splice(idx, 1);
      } else {
        this.selectedSeriesColumns.push({ name, unit: '', unit_type: 'float' });
      }
    },
    getColField(name, field) {
      const entry = this.selectedSeriesColumns.find((c) => c.name === name);
      return entry ? entry[field] : '';
    },
    setColField(name, field, value) {
      const entry = this.selectedSeriesColumns.find((c) => c.name === name);
      if (entry) entry[field] = value;
    },
    selectAllColumns() {
      const existing = new Set(this.selectedSeriesColumns.map((c) => c.name));
      for (const col of this.allPointColumns) {
        if (!existing.has(col)) {
          this.selectedSeriesColumns.push({ name: col, unit: '', unit_type: 'float' });
        }
      }
    },

    async fetchTimeseriesMdLists() {
      // no-op: md_list not required by new API
    },

    onFileSelect(e) {
      const file = e.target.files?.[0];
      if (file) this.csvFile = file;
    },

    onFileDrop(e) {
      const file = e.dataTransfer.files?.[0];
      if (file) this.csvFile = file;
    },

    async startUploadAndPreview() {
      this.importStep = 1;
      await this.uploadFile();
      if (this.uploadState !== 'done') return;
      await this.runPreview();
      if (this.previewState === 'done') this.importStep = 2;
    },

    async uploadFile() {
      this.uploadState = 'uploading';
      this.uploadStatusText = 'Initializing upload…';
      this.uploadPercent = 0;
      try {
        const initResp = await apiService.initUploadSession(
          this.entityType, this.entityId, [this.csvFile], this.entraId, null
        );
        const sessionId = initResp?.session_id;
        const uploadInfo = Array.isArray(initResp?.upload_info) ? initResp.upload_info : [];
        if (!sessionId || uploadInfo.length === 0) throw new Error('Upload initialization failed.');
        const info = uploadInfo[0];
        if (!info.upload_url) throw new Error('Missing upload URL from server.');
        this.uploadStatusText = `Uploading ${this.csvFile.name}…`;
        await apiService.uploadFileToBlob(info.upload_url, this.csvFile, (loaded, total) => {
          this.uploadPercent = total > 0 ? Math.min(99, Math.floor((loaded / total) * 100)) : 0;
        });
        this.uploadStatusText = 'Committing file…';
        const committed = await apiService.completeFileUpload(
          sessionId, info.blob_path, this.entityType, this.entityId, this.entraId, null
        );
        const fileId =
          committed?.file_id ??
          committed?.id ??
          committed?.file?.id ??
          committed?.files?.[0]?.id ??
          null;
        if (!fileId) throw new Error('Server did not return a file ID after commit.');
        this.uploadedFileId = fileId;
        this.uploadPercent = 100;
        this.uploadStatusText = 'Upload complete.';
        this.uploadState = 'done';
      } catch (err) {
        this.uploadState = 'error';
        this.uploadStatusText = 'Upload failed: ' + (err.response?.data?.detail || err.message);
        this.toast.error('File upload failed.');
        console.error('Timeseries upload error:', err);
      }
    },

    async runPreview() {
      this.previewState = 'running';
      try {
        const result = await apiService.previewTimeseriesImport({
          entraId: this.entraId,
          fileId: this.uploadedFileId,
        });
        this.previewResult = result;
        this.timeCandidates = result.detected?.time_candidates || [];

        const suggestedCandidate = this.eligibleTimeCandidates.find((candidate) => candidate.suggestedFormat) || this.eligibleTimeCandidates[0] || null;
        if (suggestedCandidate?.column) {
          this.tsColumn = suggestedCandidate.column;
          this.tsType = suggestedCandidate.suggestedFormat || suggestedCandidate.eligibleFormats?.[0] || this.tsType;
        }

        this.selectedSeriesColumns = [];
        this.previewState = 'done';
      } catch (err) {
        this.previewState = 'error';
        this.previewError = err.response?.data?.detail || err.response?.data?.message || err.message || 'Preview failed.';
        this.toast.error('CSV preview failed.');
      }
    },

    async startDryRun() {
      this.importStep = 3;
      this.importState = 'dry-running';
      try {
        const result = await apiService.runTimeseriesImport({
          entraId: this.entraId,
          fileId: this.uploadedFileId,
          entityType: this.entityType,
          entityId: Number(this.entityId),
          timestamp: this.timestampSpec,
          columns: { pointColumns: this.selectedSeriesColumns },
          dryRun: true,
        });
        this.dryRunResult = result;
        this.importState = 'idle';
      } catch (err) {
        this.importState = 'error';
        this.importError = err.response?.data?.detail || err.message || 'Dry run failed.';
        this.toast.error('Dry run failed.');
      }
    },

    async runImport() {
      this.importState = 'running';
      try {
        const result = await apiService.runTimeseriesImport({
          entraId: this.entraId,
          fileId: this.uploadedFileId,
          entityType: this.entityType,
          entityId: Number(this.entityId),
          timestamp: this.timestampSpec,
          columns: { pointColumns: this.selectedSeriesColumns },
          dryRun: false,
        });
        this.importResult = result;
        this.importState = 'done';
        this.toast.success('Timeseries data imported successfully!');
        this.fetchAvailable();
      } catch (err) {
        this.importState = 'error';
        this.importError = err.response?.data?.detail || err.message || 'Import failed.';
        this.toast.error('Timeseries import failed.');
      }
    },
  },
};
</script>

<style scoped>
.ts-panel {
  position: fixed;
  inset: 0;
  z-index: 1100;
}

.ts-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
}

.ts-modal {
  background: #EBEBE6;
  border-radius: 10px;
  width: 90vw;
  max-width: 90vw;
  max-height: 90vh;
  height: v-bind(modalHeight);
  display: flex;
  flex-direction: column;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
}

/* ── Header / Tabs ─────────────────────────────────── */
.ts-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px 0;
  border-bottom: 1px solid #c8c8c0;
}

.ts-tabs {
  display: flex;
  gap: 2px;
}

.ts-tab {
  padding: 8px 18px;
  background: none;
  border: none;
  border-bottom: 3px solid transparent;
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 500;
  color: #666;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: color 0.15s, border-color 0.15s;
}
.ts-tab:hover { color: #18392B; }
.ts-tab.active { color: #18392B; border-bottom-color: #18392B; font-weight: 700; }
.ts-tab:disabled { opacity: 0.4; cursor: not-allowed; }

.close-btn {
  background: none;
  border: none;
  cursor: pointer;
  font-size: 1.1rem;
  color: #333;
  padding: 4px 8px;
  margin-bottom: 4px;
}
.close-btn:disabled { opacity: 0.4; cursor: not-allowed; }

/* ── Body ──────────────────────────────────────────── */
.ts-body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 16px 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

/* Browse tab: controls fixed, results fills remaining space */
.ts-browse {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.browse-controls {
  flex-shrink: 0;
  padding: 16px 20px 10px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.browse-results {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 0 20px 16px;
  overflow: hidden;
}

.center-spinner {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 28px 0;
  font-size: 0.95rem;
  color: #888;
}

.alert-error {
  background: rgba(220, 53, 69, 0.1);
  border: 1px solid rgba(220, 53, 69, 0.35);
  border-radius: 6px;
  padding: 10px 14px;
  color: #dc3545;
  font-size: 0.88rem;
  display: flex;
  gap: 8px;
}

.alert-conflict {
  background: rgba(255, 165, 0, 0.1);
  border: 1px solid rgba(255, 165, 0, 0.45);
  border-radius: 6px;
  padding: 14px 16px;
  color: #7a4f00;
  font-size: 0.88rem;
  display: flex;
  gap: 12px;
  align-items: flex-start;
}

.alert-conflict-icon {
  font-size: 1.5rem;
  color: #e08000;
  flex-shrink: 0;
  line-height: 1;
}

.alert-conflict strong { font-size: 0.95rem; }
.alert-conflict p { margin: 4px 0 0; font-size: 0.85rem; }

.empty-hint {
  color: #888;
  text-align: center;
  padding: 24px 0;
  font-size: 0.92rem;
}

/* ── Browse: filter rows ───────────────────────────── */
.filter-row {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: flex-end;
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 3px;
  label { font-size: 0.78rem; font-weight: 600; color: #555; }
}

.form-control {
  padding: 5px 9px;
  border: 1px solid #c8c8c0;
  border-radius: 5px;
  background: #fff;
  color: #333;
  font-size: 0.88rem;
  box-sizing: border-box;
  min-height: 32px;
}

.form-control.sm { font-size: 0.85rem; padding: 4px 8px; min-height: 32px; }
.form-control.narrow { width: 90px; }
.form-control.w-limit  { width: 80px; }
.form-control.w-contains { width: 200px; }
.form-control.w-bool { width: 100px; }
.form-control.w-date { width: 190px; }

.attr-filters { display: flex; flex-direction: column; gap: 6px; }

.attr-filters-header {
  display: flex;
  align-items: center;
  gap: 10px;
}

.section-label { font-size: 0.82rem; font-weight: 700; color: #555; text-transform: uppercase; letter-spacing: 0.03em; }

.btn-add-filter {
  background: none;
  border: 1px solid #18392B;
  color: #18392B;
  border-radius: 4px;
  padding: 3px 10px;
  cursor: pointer;
  font-size: 0.82rem;
  display: flex;
  align-items: center;
  gap: 4px;
}
.btn-add-filter:hover { background: #18392B; color: #fff; }



.attr-filter-row {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: nowrap;
}

.attr-filter-row select.form-control {
  width: 180px;
  flex: 0 0 180px;
}

/* value inputs after the attribute select — all same height, consistent widths */
.attr-filter-row input.form-control,
.attr-filter-row select.form-control:not(:first-child) {
  flex: 0 0 auto;
}

.btn-icon-danger {
  background: none;
  border: none;
  color: #dc3545;
  cursor: pointer;
  font-size: 1.1rem;
  padding: 2px 4px;
}

.query-actions { display: flex; gap: 10px; align-items: center; }

/* ── Results table ─────────────────────────────────── */
.results-section { display: flex; flex-direction: column; gap: 6px; }

.results-meta {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 0.82rem;
  color: #666;
}

.has-more-hint { color: #e0a020; }

.btn-graph {
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 12px;
  background: #18392B;
  color: #fff;
  border: none;
  border-radius: 5px;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s;
}
.btn-graph:hover { background: #1e4d3b; }
.btn-graph i { font-size: 0.9rem; }

.results-table-wrap {
  overflow: auto;
  border: 1px solid #d8d8d0;
  border-radius: 6px;
  flex: 1;
  min-height: 0;
  scrollbar-gutter: stable;
}

/* Keep horizontal scrollbar always visible when content overflows */
.results-table-wrap::-webkit-scrollbar {
  height: 8px;
}
.results-table-wrap::-webkit-scrollbar-track {
  background: #e8e8e4;
  border-radius: 4px;
}
.results-table-wrap::-webkit-scrollbar-thumb {
  background: #b0b0a8;
  border-radius: 4px;
}
.results-table-wrap::-webkit-scrollbar-thumb:hover {
  background: #888880;
}

.results-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.82rem;
}

.results-table th {
  background: #ddddd8;
  padding: 6px 10px;
  text-align: left;
  font-weight: 700;
  position: sticky;
  top: 0;
}

.results-table td {
  padding: 4px 10px;
  border-bottom: 1px solid #e8e8e4;
}

.results-table tr:last-child td { border-bottom: none; }
.ts-cell { white-space: nowrap; font-size: 0.78rem; color: #555; }

.results-actions-row {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  padding-top: 4px;
}

.load-more-spacer { flex: 1; }

/* ── Available summary ─────────────────────────────── */
.avail-summary { font-size: 0.82rem; color: #555; }
.avail-inline { color: #333; }

/* ── Import wizard ─────────────────────────────────── */
.ts-steps {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  padding-bottom: 10px;
  border-bottom: 1px solid #d0d0c8;
}

.ts-step {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 0.8rem;
  color: #888;
  padding: 2px 8px;
  border-radius: 12px;
}
.ts-step.active { background: #18392B; color: #fff; font-weight: 700; }
.ts-step.done { color: #4caf50; }

.form-group { display: flex; flex-direction: column; gap: 4px; }
.form-group label { font-weight: 600; font-size: 0.88rem; }
.form-group .form-control { width: 100%; }
.hint { font-size: 0.78rem; color: #888; }

.file-drop-zone {
  border: 2px dashed #c8c8c0;
  border-radius: 6px;
  padding: 18px;
  text-align: center;
  cursor: pointer;
  background: #f5f5f2;
  font-size: 0.88rem;
  color: #888;
  transition: border-color 0.2s, background 0.2s;
}
.file-drop-zone:hover { border-color: #18392B; background: #eaeae5; }
.file-drop-zone i { font-size: 1.8rem; display: block; margin-bottom: 6px; }
.selected-file { color: #333; font-weight: 600; }

.file-source-toggle {
  display: flex;
  gap: 0;
  border: 1px solid #d0d0c8;
  border-radius: 6px;
  overflow: hidden;
  width: fit-content;
}
.source-btn {
  background: #f5f5f2;
  border: none;
  padding: 6px 16px;
  font-size: 0.85rem;
  cursor: pointer;
  color: #555;
  display: flex;
  align-items: center;
  gap: 5px;
  transition: background 0.15s, color 0.15s;
}
.source-btn:not(:last-child) { border-right: 1px solid #d0d0c8; }
.source-btn:hover { background: #e8e8e2; }
.source-btn.active { background: #18392B; color: #fff; }

.upload-progress-section .status-label {
  font-size: 0.9rem;
  margin-bottom: 6px;
  display: flex;
  align-items: center;
  gap: 6px;
}

.progress-bar {
  height: 8px;
  background: #e0e0e0;
  border-radius: 4px;
  overflow: hidden;
}
.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #4caf50, #66bb6a);
  transition: width 0.3s ease;
}

.preview-table { width: 100%; border-collapse: collapse; font-size: 0.88rem; margin-bottom: 8px; }
.preview-table td { padding: 5px 8px; border-bottom: 1px solid #e0e0e0; }
.preview-table td:first-child { width: 45%; color: #888; }
.text-warn { color: #e0a020; }

.parse-info-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.parse-info-item {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  font-size: 0.82rem;
}

.parse-info-item .fmt { font-weight: 600; color: #555; }
.parse-info-item .stats { color: #666; }
.parse-info-item.good .stats { color: #2e7d32; }
.parse-info-item.warn .stats { color: #b26a00; }
.parse-info-item.bad .stats { color: #c62828; }

.columns-section { display: flex; flex-direction: column; }
.columns-section-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
  font-size: 0.88rem;
}
.col-count { font-size: 0.8rem; color: #777; }

/* Scrollable column picker table */
.col-picker-table-wrap {
  max-height: 280px;
  overflow-y: auto;
  border: 1px solid #d8d8d0;
  border-radius: 6px;
  scrollbar-gutter: stable;
}
.col-picker-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.84rem;
}
.col-picker-table thead th {
  background: #ddddd8;
  padding: 5px 8px;
  text-align: left;
  font-weight: 700;
  font-size: 0.78rem;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  position: sticky;
  top: 0;
  z-index: 1;
}
.col-picker-table tbody tr {
  cursor: pointer;
  transition: background 0.1s;
}
.col-picker-table tbody tr:hover { background: #f0f0ea; }
.col-picker-table tbody tr.col-row-selected { background: #e8f5ee; }
.col-picker-table td {
  padding: 5px 8px;
  border-bottom: 1px solid #ebebea;
  vertical-align: middle;
}
.col-picker-table tbody tr:last-child td { border-bottom: none; }
.col-picker-table td input[type="checkbox"] { cursor: pointer; accent-color: #18392B; }
.col-name { font-family: monospace; font-size: 0.82rem; }
.form-control.sm { padding: 3px 6px; font-size: 0.82rem; min-height: 28px; }
.muted-type { color: #bbb; font-size: 0.78rem; }

/* Legacy chip styles kept for browse tab usage */
.chip { background: #18392B; color: #CDFAE1; border-radius: 12px; padding: 2px 10px; font-size: 0.78rem; }
.chip-check {
  background: #e8e8e4;
  color: #444;
  border-radius: 12px;
  padding: 4px 12px;
  font-size: 0.78rem;
  cursor: pointer;
  user-select: none;
  transition: background 0.15s, color 0.15s;
  border: 1px solid #c8c8c0;
}
.chip-check:hover { border-color: #18392B; }
.chip-check.selected { background: #18392B; color: #CDFAE1; border-color: #18392B; }

.import-success { text-align: center; padding: 10px 0; }
.import-success i { font-size: 2.2rem; color: #4caf50; display: block; }
.import-success h4 { margin: 6px 0 12px; }

/* ── Footer ────────────────────────────────────────── */
.ts-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 12px 20px;
  border-top: 1px solid #c8c8c0;
}

.btn-primary {
  padding: 8px 18px;
  background: #18392B;
  color: #fff;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 600;
  font-size: 0.88rem;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.btn-primary:disabled { opacity: 0.5; cursor: not-allowed; }
.btn-primary:hover:not(:disabled) { background: #1e4d3b; }

.btn-secondary {
  padding: 8px 18px;
  background: #d8d8d2;
  color: #333;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.88rem;
}
.btn-secondary:disabled { opacity: 0.5; cursor: not-allowed; }
.btn-secondary:hover:not(:disabled) { background: #c8c8c0; }

.text-danger { color: #dc3545; }

.spinner {
  display: inline-block;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
}
</style>
